<script setup lang="ts">
// The account's own key failed this answer (v3 `OrpheusKeyErrorCard.vue`, ORP-25172). It replaces
// the answer bubble: what went wrong in our words, then the provider's own words verbatim, then the
// way forward — retry for a transient failure, or answer this one question on Orphex credits and
// (for an admin) open the key settings for a key problem.
import { computed } from 'vue'

import type { OrpheusKeyError } from '../../models/orpheusKey.types'
import { creditRangeLabel, isKeyFixableError, providerName } from '../../service/orpheusApiKey'

import { formatCredits } from '@/features/credits'
import { useI18n } from '@/shared/i18n'
import { Button, Icon } from '@/shared/ui'

const props = withDefaults(defineProps<{ keyError: OrpheusKeyError; loading?: boolean }>(), {
  loading: false,
})
const emit = defineEmits<{ 'answer-with-credits': []; 'try-again': []; 'open-settings': [] }>()
const { t } = useI18n()
const K = 'orpheus.keyError'

const name = computed(
  () => providerName(props.keyError.provider) ?? t('orpheus.apiKey.theProvider'),
)
// Temporary or unexplained failures: the key isn't the problem, so only a retry is offered.
const isTransient = computed(() => !isKeyFixableError(props.keyError.type))
const canManage = computed(
  () => props.keyError.callerRole === 'owner' || props.keyError.callerRole === 'admin',
)
const rateLimited = computed(() => props.keyError.type === 'rate_limited')

const TITLE_KEYS: Record<string, string> = {
  out_of_credit: 'stopped',
  invalid_key: 'rejected',
  permission_denied: 'rejected',
  rate_limited: 'rateLimited',
  unreachable: 'unreachable',
  provider_disabled: 'disabled',
}
const title = computed(() =>
  t(`${K}.title.${TITLE_KEYS[props.keyError.type] ?? 'generic'}`, { name: name.value }),
)

const creditsMeta = computed(() => {
  const range = creditRangeLabel(props.keyError.orphexCredits)
  const remaining = props.keyError.remaining
  return [
    range ? t(`${K}.creditRange`, { range }) : '',
    remaining !== null ? t(`${K}.creditsLeft`, { count: formatCredits(remaining) }) : '',
  ]
    .filter(Boolean)
    .join(' · ')
})
</script>

<template>
  <div
    class="orpheus-key-error flex w-full flex-col gap-3 rounded-lg rounded-tl-none border p-4"
    :class="rateLimited ? 'border-warning/30 bg-warning-soft' : 'border-danger/20 bg-danger-soft'"
    :data-testid="`orpheus-key-error-${keyError.type}`"
  >
    <p
      class="m-0 flex items-center gap-2 text-sm font-semibold"
      :class="rateLimited ? 'text-warning' : 'text-danger'"
      data-testid="orpheus-key-error-title"
    >
      <Icon
        :name="isTransient ? 'circle-alert' : 'lock'"
        :size="isTransient ? 14 : 12"
        :weight="isTransient ? 'light' : 'solid'"
        aria-hidden="true"
      />
      {{ title }}
    </p>

    <!-- The provider's own words, as plain text — never our paraphrase, never HTML. -->
    <div v-if="keyError.providerMessage" class="rounded-md bg-surface/70 px-3 py-2">
      <p class="m-0 text-xs font-medium text-muted">{{ t(`${K}.said`, { name }) }}</p>
      <p
        class="m-0 whitespace-pre-wrap font-mono text-xs text-fg"
        data-testid="orpheus-key-error-provider-message"
      >
        {{ keyError.providerMessage }}
      </p>
    </div>

    <p v-if="keyError.type === 'provider_disabled'" class="m-0 text-xs text-danger">
      {{ t(`${K}.askAdmin`) }}
    </p>
    <p
      v-if="!isTransient && !keyError.orphexCreditsAvailable"
      class="m-0 text-xs text-danger"
      data-testid="orpheus-key-error-no-credits"
    >
      {{ t(`${K}.noCreditsEither`) }}
    </p>

    <div class="flex flex-wrap items-center gap-3">
      <Button
        v-if="isTransient"
        size="sm"
        :disabled="loading"
        data-testid="orpheus-key-error-try-again"
        @click="emit('try-again')"
      >
        {{ t(`${K}.tryAgain`) }}
      </Button>
      <template v-else>
        <template v-if="keyError.orphexCreditsAvailable && keyError.replayable">
          <Button
            size="sm"
            :disabled="loading"
            data-testid="orpheus-key-error-use-credits"
            @click="emit('answer-with-credits')"
          >
            {{ t(`${K}.useCredits`) }}
          </Button>
          <span v-if="creditsMeta" class="flex items-center gap-1 text-xs text-muted">
            <Icon name="coins" :size="10" weight="light" aria-hidden="true" />
            {{ creditsMeta }}
          </span>
        </template>
        <Button
          v-if="canManage"
          class="ml-auto"
          size="sm"
          variant="outline"
          data-testid="orpheus-key-error-open-settings"
          @click="emit('open-settings')"
        >
          {{ t(`${K}.openSettings`) }}
        </Button>
      </template>
    </div>
  </div>
</template>
